import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

const PENDING_REF_KEY = "dhruva_pending_partner_code";
const PENDING_INTENT_KEY = "dhruva_pending_intent";
const ANON_STORAGE_KEY = "dhruva_anon_id";

function readAnonId() {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(ANON_STORAGE_KEY) : null;
  } catch (_) { return null; }
}

function withAnonId(headers = {}) {
  const anon = readAnonId();
  return anon ? { ...headers, "X-Anon-Id": anon } : headers;
}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // `internalRole` mirrors any matching internal_users.role for this Supabase email.
  // Non-null means this email also has a portal account (partner / admin / analyst)
  // and consumer routes should bounce them back to their portal.
  const [internalRole, setInternalRole] = useState(null);
  const [internalRoleChecked, setInternalRoleChecked] = useState(false);

  // Once we have a session, opportunistically attach any pending partner referral
  // (e.g. user typed a code on signup → clicked Google → returned here).
  useEffect(() => {
    if (!session?.access_token) return;
    const code = localStorage.getItem(PENDING_REF_KEY);
    if (!code) return;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/attach-referral`, {
          method: "POST",
          headers: withAnonId({
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          }),
          body: JSON.stringify({ code }),
        });
        const data = await r.json().catch(() => ({}));
        // Whether attached or refused (already had a referral), clear the pending key.
        localStorage.removeItem(PENDING_REF_KEY);
        if (data?.attached) {
          // Light, non-blocking confirmation in console (toast import would create a cycle)
          // The Dashboard banner / partner attribution dashboard will reflect this.
          console.info("Partner referral attached:", data.partner_code);
        }
      } catch (_) {
        // Network error — keep the key so we retry on next load
      }
    })();
  }, [session?.access_token]);

  // Attach any pending signup intent (e.g. dhruva_pro_waitlist) after session
  // establishes — covers both email signup and Google OAuth redirect cases.
  useEffect(() => {
    if (!session?.access_token) return;
    const intent = localStorage.getItem(PENDING_INTENT_KEY);
    if (!intent) return;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/intent`, {
          method: "POST",
          headers: withAnonId({
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          }),
          body: JSON.stringify({ intent }),
        });
        const data = await r.json().catch(() => ({}));
        localStorage.removeItem(PENDING_INTENT_KEY);
        if (data?.attached) {
          console.info("Signup intent attached:", data.intent);
        }
      } catch (_) {
        // Network error — keep the key so we retry on next load
      }
    })();
  }, [session?.access_token]);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Fetch /api/users/me once we have a session so we know if this email also
  // owns a portal (partner/admin/analyst) account — used to gate consumer routes.
  // For portal accounts (e.g. someone clicking "Continue with Google" with their
  // partner email) we immediately sign the Supabase session back out so a stale
  // consumer session never lingers across reloads. They get a toast pointing them
  // to /portal/login. PrivateRoute's redirect is the secondary belt for any tiny
  // race window before signOut takes effect.
  useEffect(() => {
    if (!session?.access_token) {
      setInternalRole(null);
      setInternalRoleChecked(false);
      return;
    }
    let cancelled = false;
    (async () => {
      // Fresh-signup race guard (Jul 2026): when the user just signed up, a
      // burst of parallel API calls hit the backend at the same moment we
      // do — several of them race the user-row insert. If /users/me lands
      // during that race window it can 401 momentarily (Supabase admin
      // verify slow, cold start, etc.). Rather than treating a single 401
      // as "portal user, sign out", we retry a couple of times with a
      // short backoff and only conclude "no internal role" on repeat
      // failure. Prevents the "forced logout right after signup" bug that
      // production reported on 23 Jul 2026.
      let data = null;
      let lastStatus = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (cancelled) return;
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me`, {
            headers: withAnonId({ Authorization: `Bearer ${session.access_token}` }),
          });
          lastStatus = r.status;
          if (r.ok) { data = await r.json(); break; }
          // 401 during the pending-signup-OTP window is expected — stop
          // retrying, treat as "not an internal account".
          try {
            if (typeof window !== "undefined" &&
                sessionStorage.getItem("dhruva_pending_signup_otp") === "1") {
              break;
            }
          } catch (_) { /* sessionStorage may be blocked */ }
        } catch (_) { /* network — retry */ }
        // Exponential-ish backoff: 400ms, 900ms, done.
        await new Promise((res) => setTimeout(res, 400 + attempt * 500));
      }
      if (cancelled) return;
      const role = data?.internal_role || null;
      setInternalRole(role);
      if (role) {
        try {
          localStorage.removeItem(PENDING_REF_KEY);
          localStorage.removeItem(PENDING_INTENT_KEY);
        } catch (_) { /* non-critical */ }
        toast.error(
          `This account is registered as a ${role} — please log in via the portal.`,
          { duration: 6000 }
        );
        await supabase.auth.signOut();
        if (!cancelled) setSession(null);
      } else if (lastStatus && lastStatus !== 200 && lastStatus !== 401) {
        // Surface non-401 errors quietly for observability; don't sign out.
        // Users can still use the app; downstream data-fetches will trigger
        // their own toasts if truly broken.
      }
      if (!cancelled) setInternalRoleChecked(true);
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setInternalRole(null);
    setInternalRoleChecked(false);
  }, []);

  const value = useMemo(
    () => ({ session, user: session?.user || null, loading, signOut, internalRole, internalRoleChecked }),
    [session, loading, signOut, internalRole, internalRoleChecked]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
